import { type CommandProps, Extension } from '@tiptap/core';
import { closeHistory } from '@tiptap/pm/history';
import type { NodeType, Node as PMNode } from '@tiptap/pm/model';
import { type EditorState, Plugin, PluginKey, type Transaction } from '@tiptap/pm/state';

import { landOnTextblock } from '../../landOnTextblock';

// The "Paste as" prompt shown after pasting a URL. This extension owns the
// shared machinery — prompt state, keyboard navigation, and the generic
// commands — while LinkEmbed and LinkCard own their node-specific outcomes.
// It expects both nodes to be registered alongside it.

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    linkPrompt: {
      dismissLinkPrompt: () => ReturnType;
      setLinkPromptActive: (active: LinkPromptAction) => ReturnType;
    };
  }
}

export type LinkPromptAction = 'previewOnly' | 'previewAndUrl' | 'urlOnly';

export type LinkPromptState = {
  from: number;
  to: number;
  active: LinkPromptAction;
} & ({ kind: 'embed'; service: string; data: Record<string, unknown> } | { kind: 'preview' });

const PROMPT_ACTIONS: readonly LinkPromptAction[] = ['previewOnly', 'previewAndUrl', 'urlOnly'];

// Populated by LinkCard's paste handler when a solo URL is pasted.
export const linkPromptKey = new PluginKey<LinkPromptState | null>('linkPrompt');

// Swallow the separator space the paste handler adds after the link.
const linkPromptRange = (state: EditorState, prompt: LinkPromptState): { from: number; to: number } => {
  const next = state.doc.textBetween(prompt.to, Math.min(prompt.to + 1, state.doc.content.size));
  return { from: prompt.from, to: next === ' ' ? prompt.to + 1 : prompt.to };
};

// Skip past consecutive nodes of `type` starting at `at`, so a new block lands
// below any cards already trailing the paragraph.
export const afterTrailingNodes = (tr: Transaction, type: NodeType | undefined, at: number): number => {
  let pos = at;
  let next = tr.doc.nodeAt(pos);
  while (type && next?.type === type) {
    pos += next.nodeSize;
    next = tr.doc.nodeAt(pos);
  }
  return pos;
};

// Resolving the prompt is a separate undo step from the paste that opened it.
// Without this the two merge whenever the choice lands inside history's 500ms
// newGroupDelay — pasting and pressing Enter — and undo takes the URL with it.
const startNewHistoryEvent = ({ tr }: CommandProps): boolean => {
  closeHistory(tr);
  return true;
};

// Replace the prompt's link text with `content`.
export const replaceLinkPrompt = (
  props: CommandProps,
  prompt: LinkPromptState,
  content: { type: string; attrs: Record<string, unknown> },
): boolean =>
  props
    .chain()
    .command(startNewHistoryEvent)
    .insertContentAt(linkPromptRange(props.state, prompt), content)
    .command(landOnTextblock)
    .run();

// Keep the link text, insert `node` below its block (past any cards already
// trailing it) and dismiss the prompt. `node: null` just dismisses.
export const appendBelowLinkPrompt = (
  { state, tr, dispatch }: CommandProps,
  prompt: LinkPromptState,
  node: PMNode | null,
): boolean => {
  if (dispatch) {
    if (node) {
      // Survives undo today only because this inserts outside the pasted range,
      // so the two never look adjacent to history. Make that deliberate.
      closeHistory(tr);
      const at = afterTrailingNodes(tr, state.schema.nodes.linkCard, state.doc.resolve(prompt.from).after(1));
      tr.insert(at, node);
    }
    tr.setMeta(linkPromptKey, null);
  }
  return true;
};

export const LinkPrompt = Extension.create({
  name: 'linkPrompt',

  addCommands() {
    return {
      dismissLinkPrompt:
        () =>
        ({ tr, dispatch }) => {
          if (dispatch) tr.setMeta(linkPromptKey, null);
          return true;
        },
      setLinkPromptActive:
        (active) =>
        ({ state, tr, dispatch }) => {
          const prompt = linkPromptKey.getState(state);
          if (!prompt) return false;
          if (dispatch && prompt.active !== active) tr.setMeta(linkPromptKey, { ...prompt, active });
          return true;
        },
    };
  },

  addKeyboardShortcuts() {
    const cycleActive = (direction: 1 | -1) => () => {
      const prompt = linkPromptKey.getState(this.editor.state);
      if (!prompt) return false;
      const index = PROMPT_ACTIONS.indexOf(prompt.active);
      const active = PROMPT_ACTIONS[(index + direction + PROMPT_ACTIONS.length) % PROMPT_ACTIONS.length];
      return this.editor.commands.setLinkPromptActive(active);
    };

    return {
      Escape: () => {
        if (!linkPromptKey.getState(this.editor.state)) return false;
        return this.editor.commands.dismissLinkPrompt();
      },
      Tab: cycleActive(1),
      'Shift-Tab': cycleActive(-1),
      ArrowLeft: cycleActive(-1),
      ArrowRight: cycleActive(1),
      Enter: () => {
        const prompt = linkPromptKey.getState(this.editor.state);
        if (!prompt) return false;
        if (prompt.active === 'urlOnly') return this.editor.commands.dismissLinkPrompt();
        if (prompt.active === 'previewAndUrl') {
          return prompt.kind === 'embed'
            ? this.editor.commands.appendLinkPromptEmbed()
            : this.editor.commands.appendLinkPromptPreview();
        }
        return prompt.kind === 'embed'
          ? this.editor.commands.convertLinkPromptToEmbed()
          : this.editor.commands.showLinkPromptPreview();
      },
    };
  },

  addProseMirrorPlugins() {
    return [
      new Plugin<LinkPromptState | null>({
        key: linkPromptKey,
        state: {
          init: () => null,
          apply: (tr, prev) => {
            const meta = tr.getMeta(linkPromptKey);
            if (meta !== undefined) return meta;
            if (tr.selectionSet) return null;
            if (!tr.docChanged) return prev;
            if (!prev) return null;
            // Async card-metadata updates change node attrs, not text; they must not dismiss the menu.
            if (tr.doc.textContent !== tr.before.textContent) return null;
            const from = tr.mapping.map(prev.from);
            const to = tr.mapping.map(prev.to, -1);
            if (from >= to) return null;
            return { ...prev, from, to };
          },
        },
      }),
    ];
  },
});
